<template>
  <aha-modal
    noMarginPadding
    width="1084px"
    :title="modalTitle"
    :classesModal="classesModal"
    :backable="!!templateSelected"
    @back="handleBackToTemplate"
    @hide="hide"
  >
    <div class="template-library-body">
      <section v-if="!templateSelected" class="template-library-content relative">
        <div v-if="canUseTemplateLibraryEditorSearch" class="template-library-content-wrapper grid-cols-12">
          <div class="template-library-sidebar">
            <TemplateSearchV2
              ref="templateLibraryEditorSearch"
              placeholder="Search templates"
              class="pb-4"
              isInEditor
              @searchTemplate="searchTemplate"
              @clearSearchTemplate="clearSearchTemplate"
            />
            <TemplateNavbar
              :activeTab="activeCategoryId"
              :hideActiveCategory="!!searchValue"
              :categories="categories"
              :selectedCategoryLabel="selectedCategoryLabel"
              @onChange="handleCategoryChange"
            />
          </div>

          <div class="template-library-main">
            <SearchResultSection
              class="search-result-container"
              v-if="searchValue"
              isInEditor
              :noResult="noResult"
              :isSearching="isLoadingTemplates"
              :searchText="searchValue"
              :filteredPresentations="searchedTemplates"
            />
            <CategoryResultSection
              v-else
              data-max-cols="3"
              :isLoading="isLoadingTemplates"
              :categorySelected="filterTemplates"
              :title="selectedCategoryLabel"
              copyType="slides"
              :handleClickTemplate="handleClickTemplate"
            />
          </div>
        </div>
        <div v-else class="template-library-content-wrapper">
          <TemplateNavbar
            :activeTab="activeCategoryId"
            :categories="categories"
            :selectedCategoryLabel="selectedCategoryLabel"
            class="template-library-sidebar"
            @onChange="handleCategoryChange"
          />
          <div class="template-library-main">
            <CategoryResultSection
              v-if="canShowNewDiscoverTemplates"
              data-max-cols="3"
              :isLoading="isLoadingTemplates"
              :categorySelected="normalizedDiscoverTemplates"
              :title="selectedCategoryLabel"
              :copyType="'slides'"
              :handleClickTemplate="handleClickTemplate"
            />
            <CategoryResultSection
              v-else
              data-max-cols="3"
              :isLoading="isLoadingTemplates"
              :categorySelected="selectedCategoryData"
              :title="selectedCategoryLabel"
              :copyType="'slides'"
              :handleClickTemplate="handleClickTemplate"
            />
          </div>
        </div>
      </section>
      <TemplatePreview
        v-if="templateSelected"
        :presentationShareCode="templateSelected.shareCode"
        :templateLocation="'template-library'"
        :isPopupMode="true"
        :where="'presentation-editor'"
        :copyType="'slides'"
        :onCategoryChange="handleCategoryChangeInTemplatePreview"
      />
    </div>
  </aha-modal>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import TemplateNavbar from './template-container/TemplateNavbar.vue'
import CategoryResultSection from './template-container/CategoryResultSection.vue'
import TemplatePreview from '@/components-v2/template/TemplatePreview.vue'
import TemplateSearchV2 from '@/components-v2/template/TemplateSearch-v2.vue'
import SearchResultSection from './template-container/SearchResultSection.vue'

import utils from '@/util'
import constants from '@/constant'
import { TEMPLATE_ACTION } from '@/vuex/modules/templates/templates.module'

export default {
  name: 'TemplateLibraryModal',
  components: {
    TemplateSearchV2,
    SearchResultSection,
    TemplateNavbar,
    CategoryResultSection,
    TemplatePreview,
  },
  data() {
    return {
      selectedCategoryLabel: '',
      activeCategoryId: null,
      templateSelected: null,
      searchValue: '',
    }
  },
  computed: {
    ...mapState('templateCategory', {
      categories: (state) => state.categories,
      selectedCategoryData: (state) => state.selectedCategoryData,
      isLoadingFeaturedTemplates: (state) => state.isLoadingFeaturedTemplates,
      isLoadingCategoryDetail: (state) => state.isLoadingCategoryDetail,
    }),
    ...mapState({
      isShowModalTemplateLibrary: (state) => state.isShowModalTemplateLibrary,
    }),
    ...mapGetters('featureToggles', [
      'canUseReleaseNewDiscoverTemplates',
      'canUseTemplateLibraryEditorSearch',
    ]),
    ...mapGetters('templates', [
      'getDiscoverTemplates',
      'getSearchTemplates',
      'isDiscoverTemplatesLoading',
      'isTemplatesLoading',
    ]),
    modalTitle() {
      if (this.templateSelected) {
        return this.$i18nPresenter.t('Back')
      }

      return this.$i18nPresenter.t('Templates')
    },
    classesModal() {
      return {
        'template-library-modal': true,
        backable: !!this.templateSelected,
      }
    },
    isLoadingTemplates() {
      return this.isLoadingFeaturedTemplates || this.isLoadingCategoryDetail
      || this.isDiscoverTemplatesLoading || this.isTemplatesLoading(TEMPLATE_ACTION.SEARCH)
    },
    canShowNewDiscoverTemplates() {
      return this.canUseReleaseNewDiscoverTemplates && !this.activeCategoryId
    },
    normalizedDiscoverTemplates() {
      return {
        PresentationsCategories: this.getDiscoverTemplates.map((template) => ({
          ...template,
          Presentation: {
            ...template,
            shareCode: template.shareCode,
            copyCount: template.downloadCount || template.copyCount,
            isStaffPick: template.isStaffPick || template.isAdminPick,
          },
        })),
      }
    },
    searchedTemplates() {
      return this.getSearchTemplates(this.searchValue)
    },
    noResult() {
      return this.searchedTemplates.length === 0 && !this.isLoadingTemplates
    },
    filterTemplates() {
      return this.canShowNewDiscoverTemplates ? this.normalizedDiscoverTemplates : this.selectedCategoryData
    },
  },
  created() {
    this.getAllCategories()
    this.initDiscoverTemplates()
  },
  methods: {
    ...mapActions('templateCategory', [
      'getAllCategories',
      'getFeaturedTemplatesByCategories',
      'getCategoryDetail',
    ]),
    ...mapActions(['setShowModalTemplateLibraryAction']),
    hide() {
      this.setShowModalTemplateLibraryAction(false)
    },
    async handleCategoryChange(category) {
      if (this.canUseTemplateLibraryEditorSearch) {
        this.$refs.templateLibraryEditorSearch.clearSearch()
      }

      if (category.id === this.activeCategoryId) return

      this.activeCategoryId = category.id
      this.selectedCategoryLabel = category.label

      await this.scrollLibraryBodyToTop()

      if (category.id) {
        this.getCategoryDetail(category.id)
      } else {
        this.initDiscoverTemplates()
      }
    },
    clearSearchTemplate() {
      this.searchValue = ''
    },
    async scrollLibraryBodyToTop() {
      await this.$nextTick()
      const el = document.querySelector('.template-library-body')

      if (el) {
        el.scrollTop = 0
      }
    },
    handleClickTemplate(template) {
      this.templateSelected = template
    },
    handleBackToTemplate() {
      this.templateSelected = null
    },
    handleCategoryChangeInTemplatePreview(category) {
      this.templateSelected = null
      this.handleCategoryChange(category)
    },
    initDiscoverTemplates() {
      if (this.canUseReleaseNewDiscoverTemplates) {
        this.$store.dispatch('templates/fetchDiscoverTemplates')
      } else {
        this.getFeaturedTemplatesByCategories()
      }
    },
    async searchTemplate(searchValue) {
      this.searchValue = searchValue
      if (!searchValue) {
        return
      }
      this.$store.dispatch('templates/fetchSearchTemplates', { q: searchValue })
      utils.mixpanelTracking(constants.eventCategories.EDITOR_TEMPLATE_LIBRARY, 'Search Template', searchValue)
    },
  },
}
</script>

<style lang="scss">
// universal style
$modal-content-height: 660px;

.template-library-modal {
  .aha-modal__header {
    background-color: #fff;
    border-bottom: 1px solid #D3D7E1;
    position: relative;
    @apply pt-10 md:pt-4;

    .aha-modal__header--text {
      align-items: center;
      @apply mt-0;
    }

    .aha-modal__header--text-content {
      position: relative;

      &::after {
        position: absolute;
        top: 50%;
        left: calc(100% + 0.25rem);
        transform: translateY(-50%);
        background-color: #fff5fa;
        color: #fd5cad;
        content: "Beta";
        display: inline-block;
        padding-inline: 0.25rem;
        border-radius: 2px;
        font-weight: 700;
        font-size: 0.875rem;
      }
    }
  }

  &.backable {
    .aha-modal__header {
      .aha-modal__header--text {
        .aha-modal__header--text-content {
          @apply hidden md:inline-block;
        }
      }
    }
  }

  .aha-modal__content {
    background-color: #F0F4FA;
  }

  @media (min-width: 768px) {
    .template-preview {
      &.--loading {
        position: absolute;
        width: 100%;
        height: $modal-content-height;
      }

      .template-details {
        height: $modal-content-height;
        padding-top: 20px;
        overflow-y: auto;

        .staff-choice {
          top: 21px;
        }
      }

      .slides-header {
        display: none;
      }

      .template-slides.has-fixed-height {
        height: $modal-content-height;
        padding-top: 20px;
      }
    }
  }
}
</style>

<style lang="scss" scoped>
$template-library-body-height: 660px;

.template-library-body {
  @media (min-width: 768px) {
    height: $template-library-body-height;
    overflow-y: auto;
  }
}

.template-library-content {
  padding: 20px;

  .template-library-content-wrapper {
    @apply grid xl:gap-x-5;

    @media (min-width: 1280px) {
      grid-template-columns: 200px 1fr;
    }
  }

  .template-library-sidebar {
    @apply col-span-12 mb-5 xl:col-auto;
  }

  .template-library-main {
    @apply col-span-12 xl:overflow-hidden xl:col-auto;
  }
}

  ::v-deep .search-result-container {
    .templates {
      .editor-template-container {
        @apply grid-cols-1 sm:grid-cols-2;
      }
    }
  }
</style>
